import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Ban, ExternalLink, LogIn, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import type {
  AdminEventReportItem,
  AdminImpersonationResponse,
  AdminMemberListItem,
  AdminOrganizerDetail,
} from "@ticketnjoy/types";

import { CurrencyTotalsTable } from "@/components/currency-totals-table";
import { MemberManageDialog, MemberRow } from "@/components/member-manage";
import { OrganizerFinanceCard } from "@/components/organizer-finance-card";
import { OrganizerPaymentCard } from "@/components/organizer-payment-card";
import { OrganizerLegalInfoCard, OrganizerProfileCard } from "@/components/organizer-profile-cards";
import { PlatformLogRow } from "@/components/platform-log-row";
import { FullPageSpinner } from "@/components/require-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { QueryError } from "@/components/query-error";
import {
  EVENT_REPORT_PAGE_SIZE,
  useEventReportsQuery,
  useImpersonateMutation,
  useIsSuperAdmin,
  useMembersQuery,
  useOrganizerQuery,
  useRegenerateEventReportMutation,
  useSuspendMutation,
  useUnsuspendMutation,
  useUpdateFeesMutation,
} from "@/features/api";
import { ApiError, showApiError } from "@/lib/api-client";
import {
  ORGANIZER_STATUS_LABELS,
  SHOP_STATUS_LABELS,
  dateOnly,
  dateTime,
  money,
  organizerStatusVariant,
  parseDecimal,
  percent,
} from "@/lib/format";
import { cn } from "@/lib/utils";

export function OrganizerDetailPage() {
  const { id = "" } = useParams();
  const { data, isLoading, error, refetch, isRefetching } = useOrganizerQuery(id);

  if (isLoading) return <FullPageSpinner />;
  if (error || !data) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4">
        <BackLink />
        {notFound ? (
          <p className="text-sm text-destructive">Organizatör bulunamadı.</p>
        ) : (
          <QueryError
            message="Organizatör yüklenemedi."
            onRetry={() => refetch()}
            retrying={isRefetching}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{data.companyName}</h1>
            <Badge variant={organizerStatusVariant(data.status)}>
              {ORGANIZER_STATUS_LABELS[data.status] ?? data.status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {data.email} · {data.phoneNumber} · {data.memberCount} üye · Kayıt{" "}
            {dateOnly(data.createdAt)}
          </p>
        </div>
      </div>

      {data.status === "Suspended" && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
          <div className="font-medium text-destructive">
            Hesap {dateTime(data.suspendedAt)} tarihinde askıya alındı.
          </div>
          {data.suspensionReason && (
            <div className="mt-1 text-muted-foreground">Sebep: {data.suspensionReason}</div>
          )}
          <div className="mt-1 text-muted-foreground">
            Panel girişi ve açık oturumlar kapalı; mağazalar alıcılara görünmüyor. Mevcut
            siparişler ve biletler etkilenmez.
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <OrganizerFinanceCard organizerId={data.id} legalName={data.legalName} />

          <Card>
            <CardHeader>
              <CardTitle>Satış</CardTitle>
              <CardDescription>Tüm zamanlar, iade sonrası net.</CardDescription>
              <CardAction>
                <Link
                  to={`/orders?organizerId=${data.id}`}
                  className="text-sm whitespace-nowrap text-muted-foreground hover:text-foreground"
                >
                  Siparişleri gör
                </Link>
              </CardAction>
            </CardHeader>
            <CardContent>
              <CurrencyTotalsTable rows={data.sales} emptyText="Henüz satış yok." />
            </CardContent>
          </Card>

          <EventReportsCard organizerId={data.id} />

          <Card>
            <CardHeader>
              <CardTitle>Mağazalar</CardTitle>
            </CardHeader>
            <CardContent>
              {data.shops.length === 0 ? (
                <p className="text-sm text-muted-foreground">Mağaza yok.</p>
              ) : (
                <ul className="divide-y text-sm">
                  {data.shops.map((s) => (
                    <li key={s.id} className="flex items-center justify-between py-2">
                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-muted-foreground">/{s.slug}</div>
                      </div>
                      <Badge variant={s.status === "Active" ? "success" : "secondary"}>
                        {SHOP_STATUS_LABELS[s.status] ?? s.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <MembersCard organizerId={data.id} />

          <Card>
            <CardHeader>
              <CardTitle>Platform işlemleri</CardTitle>
              <CardDescription>Bu hesapta admin'lerin yaptığı son değişiklikler.</CardDescription>
              {data.recentActions.length > 0 && (
                <CardAction>
                  <Link
                    to={`/audit-logs?organizerId=${data.id}`}
                    className="text-sm whitespace-nowrap text-muted-foreground hover:text-foreground"
                  >
                    Tümünü gör
                  </Link>
                </CardAction>
              )}
            </CardHeader>
            <CardContent>
              {data.recentActions.length === 0 ? (
                <p className="text-sm text-muted-foreground">Henüz işlem yok.</p>
              ) : (
                <ul className="divide-y text-sm">
                  {data.recentActions.map((a) => (
                    <PlatformLogRow
                      key={a.id}
                      action={a.action}
                      actorEmail={a.actorEmail}
                      diff={a.diff}
                      createdAt={a.createdAt}
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 space-y-6">
          <OrganizerProfileCard organizer={data} />
          <FeesCard key={data.id} organizer={data} />
          <OrganizerPaymentCard organizerId={data.id} />
          <StatusCard organizer={data} />
          <ImpersonateCard organizer={data} />
          <OrganizerLegalInfoCard organizer={data} />
        </div>
      </div>
    </div>
  );
}

function MembersCard({ organizerId }: { organizerId: string }) {
  const { data, isLoading, error, refetch, isRefetching } = useMembersQuery({
    q: "",
    role: "",
    status: "",
    organizerId,
    page: 1,
    pageSize: 100,
  });
  const [selected, setSelected] = useState<AdminMemberListItem | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kullanıcılar</CardTitle>
        <CardDescription>Panele giren ekip; sahip en üstte.</CardDescription>
      </CardHeader>
      <CardContent className="px-0">
        {error ? (
          <QueryError
            className="mx-6"
            message="Kullanıcılar yüklenemedi."
            onRetry={() => refetch()}
            retrying={isRefetching}
          />
        ) : isLoading || !data ? (
          <p className="px-6 text-sm text-muted-foreground">Yükleniyor...</p>
        ) : (
          <ul className="divide-y border-t text-sm">
            {data.items.map((m) => (
              <MemberRow key={m.id} member={m} showOrganizer={false} onManage={() => setSelected(m)} />
            ))}
          </ul>
        )}
      </CardContent>
      <MemberManageDialog member={selected} onClose={() => setSelected(null)} />
    </Card>
  );
}

function BackLink() {
  return (
    <Link
      to="/organizers"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      Organizatörler
    </Link>
  );
}

function FeesCard({ organizer }: { organizer: AdminOrganizerDetail }) {
  const isSuper = useIsSuperAdmin();
  const update = useUpdateFeesMutation(organizer.id);
  const [commission, setCommission] = useState(String(organizer.commissionRate));
  const [fee, setFee] = useState(String(organizer.serviceFee));

  const commissionValue = parseDecimal(commission);
  const feeValue = parseDecimal(fee);
  const valid =
    commissionValue !== null &&
    commissionValue >= 0 &&
    commissionValue <= 99.99 &&
    feeValue !== null &&
    feeValue >= 0 &&
    feeValue <= 10000;
  const dirty =
    valid && (commissionValue !== organizer.commissionRate || feeValue !== organizer.serviceFee);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty || commissionValue === null || feeValue === null) return;
    try {
      await update.mutateAsync({ commissionRate: commissionValue, serviceFee: feeValue });
      toast.success("Ücretler güncellendi. Yeni siparişlerde geçerli.");
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Komisyon ve servis ücreti</CardTitle>
        <CardDescription>
          Sadece yeni siparişlere uygulanır; verilmiş siparişlerin tutarı değişmez.
          Organizatör değişikliği kendi denetim kaydında görür.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isSuper ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="commission">Komisyon oranı (%)</Label>
              <Input
                id="commission"
                inputMode="decimal"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fee">Bilet başı servis ücreti</Label>
              <Input
                id="fee"
                inputMode="decimal"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Ücretsiz (0 TL) biletlerde alınmaz.</p>
            </div>
            {!valid && (
              <p className="text-xs text-destructive">
                Komisyon 0–99,99, servis ücreti 0–10.000 arasında olmalı.
              </p>
            )}
            <Button type="submit" className="w-full" disabled={!dirty || update.isPending}>
              {update.isPending ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </form>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Komisyon</span>
              <span className="tabular-nums">{percent(organizer.commissionRate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Servis ücreti</span>
              <span className="tabular-nums">
                {organizer.serviceFee.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
              </span>
            </div>
            <p className="pt-2 text-xs text-muted-foreground">
              Değiştirmek için süper admin yetkisi gerekir.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusCard({ organizer }: { organizer: AdminOrganizerDetail }) {
  const isSuper = useIsSuperAdmin();
  const suspend = useSuspendMutation(organizer.id);
  const unsuspend = useUnsuspendMutation(organizer.id);
  const [open, setOpen] = useState(false);
  const [unsuspendOpen, setUnsuspendOpen] = useState(false);
  const [reason, setReason] = useState("");

  const isSuspended = organizer.status === "Suspended";
  const isDeleted = organizer.status === "Deleted";

  async function onSuspend() {
    try {
      await suspend.mutateAsync({ reason: reason.trim() });
      toast.success("Hesap askıya alındı.");
      setOpen(false);
      setReason("");
    } catch (err) {
      showApiError(err);
    }
  }

  async function onUnsuspend() {
    try {
      await unsuspend.mutateAsync();
      toast.success("Hesap tekrar açıldı.");
      setUnsuspendOpen(false);
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hesap durumu</CardTitle>
        <CardDescription>
          Askıya alınan hesap panele giremez, mağazaları satışa kapanır.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isSuper ? (
          <p className="text-xs text-muted-foreground">
            Askıya almak için süper admin yetkisi gerekir.
          </p>
        ) : isDeleted ? (
          <p className="text-sm text-muted-foreground">Silinmiş hesap.</p>
        ) : isSuspended ? (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setUnsuspendOpen(true)}
          >
            <RotateCcw className="size-4" />
            Askıyı kaldır
          </Button>
        ) : (
          <Button variant="destructive" className="w-full" onClick={() => setOpen(true)}>
            <Ban className="size-4" />
            Askıya al
          </Button>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen} disableBackdropClose>
        <DialogContent>
          <DialogHeader
            title={`${organizer.companyName} askıya alınsın mı?`}
            description="Panel oturumları hemen düşer, mağazalar ve satış linkleri kapanır. Mevcut siparişler, biletler ve ödeme dönüşleri çalışmaya devam eder."
          />
          <div className="space-y-2">
            <Label htmlFor="reason">Sebep</Label>
            <Textarea
              id="reason"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Organizatör kendi denetim kaydında bu metni görür."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button
              variant="destructive"
              onClick={onSuspend}
              disabled={suspend.isPending || reason.trim().length < 3}
            >
              {suspend.isPending ? "Askıya alınıyor..." : "Askıya al"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unsuspendOpen} onOpenChange={setUnsuspendOpen}>
        <DialogContent>
          <DialogHeader
            title={`${organizer.companyName} askıdan çıkarılsın mı?`}
            description={
              organizer.emailVerifiedAt
                ? "Organizatör panele tekrar girebilir; mağazaları ve satış linkleri askıdan önceki haline döner. Askı sebebi kalkar, kayıtlarda kalır."
                : "Mağazaları ve satış linkleri askıdan önceki haline döner. E-postasını doğrulamadığı için panele girmeden önce doğrulama ister. Askı sebebi kalkar, kayıtlarda kalır."
            }
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnsuspendOpen(false)}>
              Vazgeç
            </Button>
            <Button onClick={onUnsuspend} disabled={unsuspend.isPending}>
              {unsuspend.isPending ? "Açılıyor..." : "Askıyı kaldır"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ImpersonateCard({ organizer }: { organizer: AdminOrganizerDetail }) {
  const isSuper = useIsSuperAdmin();
  const impersonate = useImpersonateMutation(organizer.id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [session, setSession] = useState<AdminImpersonationResponse | null>(null);

  const blocked = organizer.status === "Suspended" || organizer.status === "Deleted";

  async function onStart() {
    try {
      setSession(await impersonate.mutateAsync({ reason: reason.trim() }));
      setReason("");
    } catch (err) {
      showApiError(err);
    }
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setSession(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Destek oturumu</CardTitle>
        <CardDescription>
          Organizatörün panelini sahibi yetkisiyle açar. Organizatör oturumu ve yapılan her
          işlemi kendi kayıtlarında "TicketNjoy" olarak görür.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isSuper ? (
          <p className="text-xs text-muted-foreground">Süper admin yetkisi gerekir.</p>
        ) : blocked ? (
          <p className="text-xs text-muted-foreground">
            Askıdaki veya silinmiş hesaba destek oturumu açılamaz.
          </p>
        ) : (
          <Button variant="outline" className="w-full" onClick={() => setOpen(true)}>
            <LogIn className="size-4" />
            Organizatör olarak gir
          </Button>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose>
        <DialogContent>
          {session ? (
            <>
              <DialogHeader
                title="Destek oturumu açıldı"
                description={`Oturum ${dateTime(session.expiresAt)} tarihine kadar geçerli. Panelde üstteki banttan oturumu kapatabilirsin.`}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Kapat
                </Button>
                <Button asChild>
                  <a href={session.dashboardUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-4" />
                    Paneli aç
                  </a>
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader
                title={`${organizer.companyName} paneline girilsin mi?`}
                description="1 saatlik oturum açılır ve sahibi yetkisiyle tüm işlemleri yapabilirsin (iade, etkinlik, ödeme talebi dahil). Bu tarayıcıdaki mevcut organizatör paneli oturumu kapanır."
              />
              <div className="space-y-2">
                <Label htmlFor="impersonate-reason">Sebep</Label>
                <Textarea
                  id="impersonate-reason"
                  rows={3}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Organizatör işlem geçmişinde bu metni görür."
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Vazgeç
                </Button>
                <Button
                  onClick={onStart}
                  disabled={impersonate.isPending || reason.trim().length < 3}
                >
                  {impersonate.isPending ? "Açılıyor..." : "Oturumu aç"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function EventReportsCard({ organizerId }: { organizerId: string }) {
  const isSuper = useIsSuperAdmin();
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, error, refetch } = useEventReportsQuery(organizerId, page);
  const regenerate = useRegenerateEventReportMutation(organizerId);
  const [target, setTarget] = useState<AdminEventReportItem | null>(null);
  const pageCount = data ? Math.max(1, Math.ceil(data.total / EVENT_REPORT_PAGE_SIZE)) : 1;

  async function onRegenerate() {
    if (!target) return;
    try {
      await regenerate.mutateAsync(target.eventId);
      toast.success("Rapor yeniden üretildi.");
      setTarget(null);
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Etkinlik raporları</CardTitle>
        <CardDescription>
          Bitmiş etkinlikler. Rapor her gece otomatik üretilir; elle yeniden üretim organizatörün
          kaydında "TicketNjoy" olarak görünür.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <QueryError message="Etkinlikler yüklenemedi." onRetry={() => refetch()} retrying={isFetching} />
        ) : isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Yükleniyor...</p>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Bitmiş etkinlik yok.</p>
        ) : (
          <ul className={cn("divide-y text-sm", isFetching && "opacity-60")}>
            {data.items.map((e) => (
              <li key={e.eventId} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <Link
                    to={`/orders?organizerId=${organizerId}&eventId=${e.eventId}`}
                    className="font-medium break-words hover:underline"
                  >
                    {e.title}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    Bitiş {dateOnly(e.endsAt)} ·{" "}
                    {e.snapshotTakenAt
                      ? `Rapor ${dateTime(e.snapshotTakenAt)}`
                      : "Rapor henüz üretilmedi"}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {e.currency && e.ticketsSold !== null && e.organizerNetRevenue !== null && (
                    <div className="text-right text-xs text-muted-foreground tabular-nums">
                      <div>{e.ticketsSold} bilet</div>
                      <div title="Organizatör net geliri">
                        {money(e.organizerNetRevenue, e.currency)}
                      </div>
                    </div>
                  )}
                  {isSuper && (
                    <Button variant="outline" size="sm" onClick={() => setTarget(e)}>
                      <RefreshCw className="size-4" />
                      {e.snapshotTakenAt ? "Yeniden üret" : "Üret"}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {pageCount > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Önceki
            </Button>
            <span className="text-muted-foreground">
              {page} / {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pageCount}
              onClick={() => setPage(page + 1)}
            >
              Sonraki
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={target !== null} onOpenChange={(next) => !next && setTarget(null)}>
        <DialogContent>
          <DialogHeader
            title={`"${target?.title ?? ""}" raporu yeniden üretilsin mi?`}
            description="Rapor siparişlerin bugünkü durumuyla (iadeler ve check-in dahil) baştan hesaplanır, önceki rapor silinir. Organizatör bunu kendi kaydında görür."
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>
              Vazgeç
            </Button>
            <Button onClick={onRegenerate} disabled={regenerate.isPending}>
              {regenerate.isPending ? "Üretiliyor..." : "Yeniden üret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
